import { Gift } from 'lucide-react';

/**
 * Lets a patient spend rewards points for a discount on the OTC cart
 * they're about to check out — the same balance/ratio/minimum rules POS
 * redemption enforces, applied to their *next* order instead of at a
 * cashier till. Hidden entirely when there's nothing meaningful to redeem.
 * Fully controlled by `points`/`onChange` so it stays in sync if the cart
 * total changes (e.g. a quantity edit) while redemption is active.
 */
export function RewardsRedeem({ account, points, onChange, subtotalMinor, currency }) {
  const ratio = account?.redeem_ratio || 0;
  const minPoints = account?.min_redeem_points || 0;
  const maxByTotal = ratio > 0 ? Math.floor((subtotalMinor / 100) * ratio) : 0;
  const maxPoints = Math.min(account?.balance || 0, maxByTotal);
  if (!account || ratio <= 0 || maxPoints < minPoints) return null;

  const open = points > 0;
  const discount = open ? Math.min(subtotalMinor, Math.round((points / ratio) * 100)) : 0;
  const toggle = (checked) => onChange(checked ? Math.min(Math.max(minPoints, 1), maxPoints) : 0);

  return (
    <div className="portal-redeem-box" data-testid="otc-redeem-box">
      <label className="portal-check">
        <input type="checkbox" checked={open} onChange={(e) => toggle(e.target.checked)} data-testid="otc-redeem-toggle" />
        <Gift size={15} aria-hidden="true" /> Use points for a discount ({(account.balance || 0).toLocaleString()} pts available)
      </label>
      {open && (
        <div className="portal-redeem-controls" data-testid="otc-redeem-controls">
          <input type="range" min={Math.max(minPoints, 1)} max={Math.max(maxPoints, minPoints, 1)} step={1}
            value={Math.min(points, maxPoints)} onChange={(e) => onChange(Number(e.target.value))}
            data-testid="otc-redeem-slider" />
          <div className="flex justify-between text-sm">
            <span data-testid="otc-redeem-points">{points} pts</span>
            <span className="font-semibold text-emerald-700" data-testid="otc-redeem-discount">
              -{currency} {(discount / 100).toFixed(2)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
